import {
  decimalSeparatorOf,
  formatMoney,
  parseAmount
} from "@/helpers/money/money"
import { ICompactSuffixes, ICounterAmount } from "@/helpers/money/types"

export const COMPACT_FROM_WHOLE_UNITS = 10_000_000n

const COMPACT_SIGNIFICANT_DIGITS = 3

const SUFFIX_GROUPS: readonly (keyof ICompactSuffixes)[] = [
  "thousand",
  "million",
  "billion",
  "trillion",
  "quadrillion",
  "quintillion"
]

/** Compacts from `COMPACT_FROM_WHOLE_UNITS` whole units, floored to three significant digits. `null` for a malformed amount. */
export const formatCounterAmount = (
  amount: string,
  scale: number,
  locale: string,
  suffixes: ICompactSuffixes
): ICounterAmount | null => {
  const parsed = parseAmount(amount)
  const exactText = formatMoney(amount, scale, locale)
  if (parsed === null || exactText === null) {
    return null
  }
  const whole = parsed / 10n ** BigInt(scale)
  if (whole < COMPACT_FROM_WHOLE_UNITS) {
    return { text: exactText, exactText: null }
  }
  const digits = whole.toString()
  const group = Math.min(
    Math.floor((digits.length - 1) / 3),
    SUFFIX_GROUPS.length
  )
  const integerDigits = digits.length - group * 3
  const significant = digits.slice(
    0,
    Math.max(COMPACT_SIGNIFICANT_DIGITS, integerDigits)
  )
  const integerPart = significant.slice(0, integerDigits)
  const fraction = significant.slice(integerDigits).replace(/0+$/, "")
  const number =
    fraction === ""
      ? integerPart
      : `${integerPart}${decimalSeparatorOf(locale)}${fraction}`
  return { text: `${number}${suffixes[SUFFIX_GROUPS[group - 1]]}`, exactText }
}

const groupCeiling = (groupIndex: number): bigint =>
  1000n ** BigInt(groupIndex + 2)

const REACHABLE_SUFFIX_GROUPS = SUFFIX_GROUPS.filter(
  (_, groupIndex) => groupCeiling(groupIndex) > COMPACT_FROM_WHOLE_UNITS
)

/** The candidates for the widest text a counter at `scale` can show: the largest full amount, then each reachable suffix after the widest mantissa. */
export const counterWidthTexts = (
  scale: number,
  locale: string,
  suffixes: ICompactSuffixes
): string[] => {
  const largestFull = `${COMPACT_FROM_WHOLE_UNITS - 1n}${"9".repeat(scale)}`
  const mantissa = `9${decimalSeparatorOf(locale)}99`
  return [
    formatMoney(largestFull, scale, locale) ?? "",
    ...REACHABLE_SUFFIX_GROUPS.map((group) => `${mantissa}${suffixes[group]}`)
  ]
}
